import posthog, { type Properties } from 'posthog-js';
import { hasAnalyticsConsent } from '@/composables/useCookieConsent';
import type { EventName } from '@/posthog/events';

const isEnabled = Boolean(import.meta.env.VITE_POSTHOG_KEY);
let initialized = false;

export function initPostHog(): boolean {
    if (!isEnabled || !hasAnalyticsConsent()) {
        return false;
    }

    if (initialized) {
        posthog.opt_in_capturing();

        return true;
    }

    posthog.init(import.meta.env.VITE_POSTHOG_KEY ?? '', {
        api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com',
        ui_host: 'https://eu.posthog.com',
        defaults: '2026-01-30',
    });

    initialized = true;

    return true;
}

export function disablePostHog(): void {
    if (!initialized) {
        return;
    }

    posthog.opt_out_capturing();
}

function canCapture(): boolean {
    return isEnabled && initialized && hasAnalyticsConsent();
}

export function usePostHog() {
    function capture(event: EventName, properties: Properties = {}): void {
        if (!canCapture()) {
            return;
        }

        posthog.capture(event, properties);
    }

    function identify(distinctId: string, properties: Properties = {}): void {
        if (!canCapture()) {
            return;
        }

        posthog.identify(distinctId, properties);
    }

    function captureException(error: unknown): void {
        if (!canCapture()) {
            return;
        }

        posthog.captureException(error);
    }

    function identifyAnonymous(token: string, displayName: string): void {
        if (!canCapture()) {
            return;
        }

        posthog.identify(`anon_${token}`, { display_name: displayName });
    }

    function reset(): void {
        if (!canCapture()) {
            return;
        }

        posthog.reset();
    }

    return { capture, identify, identifyAnonymous, captureException, reset };
}
